import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Field } from '../ui/Field';
import { btnDestructive, btnGhost, btnSecondary, inputCls } from '../ui/styles';
import { StaffAccess } from './StaffAccess';
import { useReauth } from '../useReauth';
import { inviteStaff, revokeStaffLogin, stopStaff } from '../adminApi';
import { ApiError } from '../../core/api/http';
import { isValidEmail } from '../../core/rules/contact';
import { initialsOf } from '../../core/rules/salonDays';
import type { StaffProfile } from '../../core/types/site';
import type { StaffLogin } from '../../core/types/adminRequests';
import type { Treatment } from '../../core/types/catalog';
type Props = {
  profile: StaffProfile;
  treatments: Treatment[];
  login: StaffLogin | null;
  loginsUnavailable: boolean;
  onChange: (patch: Partial<StaffProfile>) => void;
  onLogin: (login: StaffLogin | null) => void;
  /** Sarahs eigen profiel staat bij Salon & contact › Over jou. */
  onOpenProfile: () => void;
};
const dot: Record<StaffProfile['color'], string> = {
  clay: 'bg-clay',
  sage: 'bg-sage',
  rose: 'bg-blush',
  ink: 'bg-ink'
};

/**
 * Eén medewerker: naam, behandelingen, de login, en stoppen. Stoppen is één handeling: login en
 * sessies meteen weg, het profiel (rooster, online boeken) na Opslaan. Sarah zelf staat vast.
 */
export function StaffRow({
  profile: s,
  treatments,
  login,
  loginsUnavailable,
  onChange,
  onLogin,
  onOpenProfile
}: Props) {
  const owner = s.role === 'owner';
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmStop, setConfirmStop] = useState(false);
  const {
    guard,
    dialog
  } = useReauth('Toegang aanpassen vraagt een extra bevestiging.');
  const invite = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) return setError('Vul een geldig e-mailadres in.');
    setBusy(true);
    setError('');
    try {
      onLogin(await inviteStaff(s.id, email.trim()));
      setEmail('');
      toast(`Uitnodiging verstuurd naar ${email.trim()}`, {
        description: `${s.name} kiest zelf een wachtwoord en stelt tweestapsverificatie in.`
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Uitnodigen lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  /** Stoppen in één handeling: login en alle sessies meteen weg (idempotent), het profiel na Opslaan. */
  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (login) {
        const res = await guard(() => stopStaff(s.id));
        if (!res.done) return;
        onLogin(null);
      }
      onChange({
        active: false
      });
      setConfirmStop(false);
      toast(`${s.name} is gestopt`, {
        description: login ? 'Ze is overal uitgelogd en kan niet meer inloggen. Sla op om haar uit het rooster te halen.' : 'Sla op om haar uit het rooster te halen.'
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Stoppen lukte niet: de login is nog actief. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await guard(() => revokeStaffLogin(s.id));
      if (!res.done) return;
      onLogin(null);
      toast(`${s.name} kan niet meer inloggen`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Intrekken lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  return <li className="space-y-4 py-4 first:pt-0 last:pb-0" data-id="element-4184" data-mp="2d25a6hz97piu">
      <div className="flex items-center gap-3" data-id="element-4185" data-mp="2d3oq27gzzyk3">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-medium text-shell ${dot[s.color]}`} aria-hidden="true" data-id="element-4186" data-mp="2r02b1qkqqi3b">
          {s.initials}
        </span>
        {owner ? <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-4187" data-mp="wnm4eoo191xx">
            <p className="text-[14px] text-ink" data-id="element-4188" data-mp="rpu2d1tobzg0">
              {s.name} <span className="text-muted" data-id="element-4189" data-mp="u3fpxn1slt34">· eigenaar, doet alle behandelingen</span>
            </p>
            <button type="button" onClick={onOpenProfile} className="text-[13px] text-ink underline underline-offset-2 hover:text-clay" data-id="element-4190" data-mp="y3huq52tr2mm">
              Je profiel aanpassen
            </button>
          </div> : <Field label="Naam" className="min-w-0 flex-1" data-id="element-4191" data-mp="1814aetry6y7n">
            <input value={s.name} onChange={e => onChange({
          name: e.target.value,
          initials: initialsOf(e.target.value)
        })} className={inputCls} autoComplete="off" data-id="element-4192" data-mp="aeyjphse4bqu" />
          </Field>}
      </div>

      {!owner && <>
          <fieldset data-id="element-4193" data-mp="p4385gps3c7e">
            <legend className="text-[13px] text-ink" data-id="element-4194" data-mp="p4cxr36wvhck">Behandelingen</legend>
            <label className="mt-2 flex items-center gap-2.5 text-[14px] text-ink" data-id="element-4195" data-mp="3nu3cotg226jo">
              <input type="checkbox" checked={s.allTreatments} onChange={e => onChange({
            allTreatments: e.target.checked
          })} className="h-4 w-4 accent-clay" data-id="element-4196" data-mp="3ssekyzqpc8zs" />
              Alle behandelingen
            </label>
            {!s.allTreatments && <div className="mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-4197" data-mp="3galpn63lqyid">
                {treatments.map(t => <label key={t.slug} className="flex items-center gap-2.5 text-[13px] text-ink" data-id="element-4198" data-mp="3bd2bs7atgnqy">
                    <input type="checkbox" checked={s.treatments.includes(t.slug)} onChange={e => onChange({
              treatments: e.target.checked ? [...s.treatments, t.slug] : s.treatments.filter(x => x !== t.slug)
            })} className="h-4 w-4 accent-clay" data-id="element-4199" data-mp="36dz5ykn0idhx" />
                    {t.name}
                  </label>)}
              </div>}
            {!s.allTreatments && s.treatments.length === 0 && <p className="mt-1.5 text-[12px] text-clay" data-id="element-4200" data-mp="2em04xjv0kkif">Zonder behandelingen kan niemand online bij {s.name} boeken.</p>}
          </fieldset>

          <fieldset className="space-y-3" data-id="element-4201" data-mp="28yg3yocbq0o7">
            <legend className="text-[13px] text-ink" data-id="element-4202" data-mp="2909am3mxskw7">Op de website</legend>
            <p className="text-[12px] text-muted" data-id="element-4203" data-mp="1uzotdbwo6x4t">Op de Over-pagina en bij "Bij wie wil je komen?" in het boeken. Zonder foto tonen we haar initialen.</p>
            <Field label="Foto (link)" data-id="element-4204" data-mp="1zzunqzubqxql">
              <input type="url" value={s.photo ?? ''} onChange={e => onChange({
            photo: e.target.value || undefined
          })} placeholder="https://…" className={inputCls} data-id="element-4205" data-mp="2nnc664rp8jgw" />
            </Field>
            <Field label="Eén zin" hint="Bij de keuze in het boeken, bijv. “Gespecialiseerd in rosacea en roodheid”." data-id="element-4206" data-mp="2dyatanggai75">
              <input value={s.intro ?? ''} maxLength={90} onChange={e => onChange({
            intro: e.target.value || undefined
          })} className={inputCls} data-id="element-4207" data-mp="2ivu6rl5bqqy1" />
            </Field>
            <Field label="Over haar" hint="Een korte alinea voor de Over-pagina." data-id="element-4208" data-mp="3bcwrgubbrstc">
              <textarea value={s.bio ?? ''} rows={3} maxLength={600} onChange={e => onChange({
            bio: e.target.value || undefined
          })} className={inputCls} data-id="element-4209" data-mp="3beztkcpr5cef" />
            </Field>
          </fieldset>

          <div className="rounded-md bg-shell px-3 py-3" data-id="element-4210" data-mp="3gokcpsn1vhys">
            <p className="text-[13px] text-ink" data-id="element-4211" data-mp="3uggr6tvsptcp">Inloggen in het beheer</p>
            {loginsUnavailable ? <p className="mt-1 text-[13px] text-muted" data-id="element-4212" data-mp="3pj8h7gkswvmj">De logins konden niet geladen worden. Ververs de pagina om het opnieuw te proberen.</p> : login ? <>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[13px]" data-id="element-4213" data-mp="2xsu8dyrna9hq">
                  <span className="text-muted" data-id="element-4214" data-mp="3bs3h6ddiu1bo">
                    {login.email} · {login.status === 'uitgenodigd' ? 'uitgenodigd, nog niet ingelogd' : 'actief'}
                  </span>
                  <button type="button" onClick={() => void revoke()} disabled={busy} className={btnGhost} data-id="element-4215" data-mp="r0097x1p7de2">
                    Login intrekken
                  </button>
                </div>
                {/* key: na een antwoord van de server begint het formulier opnieuw bij wat er nu staat. */}
                <StaffAccess key={`${(login.roles ?? []).join(',')}|${login.validUntil ?? ''}`} name={s.name} staffId={s.id} login={login} onLogin={onLogin} guard={guard} data-id="element-4216" data-mp="2kktnfnkqmbrg" />
              </> : <form onSubmit={invite} noValidate className="mt-2 flex flex-wrap items-start gap-2" data-id="element-4217" data-mp="2zasmdsk49r7z">
                <label className="sr-only" htmlFor={`invite-${s.id}`} data-id="element-4218" data-mp="349vszhfqw5i2">
                  E-mailadres van {s.name}
                </label>
                <input id={`invite-${s.id}`} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="e-mailadres" className={`${inputCls} min-w-0 flex-1`} aria-invalid={Boolean(error)} aria-describedby={error ? `invite-${s.id}-error` : undefined} disabled={!s.active} data-id="element-4219" data-mp="3d8zd8oqneqy5" />
                <button type="submit" disabled={busy || !s.active} aria-busy={busy} className={btnSecondary} data-id="element-4220" data-mp="16idy69hs5oh3">
                  {busy ? 'Versturen…' : 'Uitnodigen'}
                </button>
                {error && <p id={`invite-${s.id}-error`} role="alert" className="basis-full text-[12px] text-clay" data-id="element-4221" data-mp="1u9jdmnjt6eqv">
                    {error}
                  </p>}
              </form>}
          </div>

          {s.active ? confirmStop ? <div role="alertdialog" aria-labelledby={`stop-${s.id}`} className="rounded-md bg-shell px-3 py-3" data-id="element-4222" data-mp="1ga0eulcw0fr8">
                <p id={`stop-${s.id}`} className="text-[13px] text-ink" data-id="element-4223" data-mp="8w6tkzlh7w4q">
                  {s.name} stoppen?
                </p>
                <p className="mt-1 text-[13px] text-muted" data-id="element-4224" data-mp="4ts6226v4874">
                  {login ? 'Haar login wordt meteen ingetrokken. ' : ''}Na Opslaan staat ze niet meer in het rooster en kan niemand online bij haar boeken. Oude afspraken houden
                  haar naam; komende afspraken verzet je met Afwezig melden in de Agenda.
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-4225" data-mp="1wpsv3qbnj02v">
                  <button type="button" onClick={() => void stop()} disabled={busy} aria-busy={busy} className={btnDestructive} data-id="element-4226" data-mp="1rwg79iesqxb6">
                    {busy ? 'Stoppen…' : 'Stoppen'}
                  </button>
                  <button type="button" onClick={() => setConfirmStop(false)} disabled={busy} className={btnGhost} data-id="element-4227" data-mp="21mj5py4ot3gw">
                    Annuleren
                  </button>
                </div>
              </div> : <button type="button" onClick={() => setConfirmStop(true)} className="text-[13px] text-muted underline underline-offset-2 hover:text-clay" data-id="element-4228" data-mp="2alwgcu4jry37">
                {s.name} stopt bij de salon…
              </button> : <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted" data-id="element-4229" data-mp="2ry37gra81hpt">
              Gestopt: niet in het rooster, geen login.
              <button type="button" onClick={() => onChange({
          active: true
        })} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-4230" data-mp="2j4q3muamcvoy">
                Weer actief maken
              </button>
            </p>}
        </>}
      {dialog}
    </li>;
}